// src/pages/Campaigns/EventPost/EventPostCategories.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Stack, Chip, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ArrowBack as BackIcon, Category as CategoryIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';

const PRESET_COLORS = [
  '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b',
  '#27ae60', '#8e44ad', '#2980b9', '#d35400', '#7f8c8d'
];

const EventPostCategories = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Permissions
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Dialog state
  const [dialog, setDialog] = useState({ open: false, category: null });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3498db'
  });

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar({ ...snackbar, open: false }), 3000);
  };

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const modifyPerm = await hasPermission('volunteer_management', 'add|edit');
        const deletePerm = await hasPermission('volunteer_management', 'delete');
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (chatbotId) {
      loadCategories();
    }
  }, [chatbotId]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/event-post-categories', {
        params: { chatbot_id: chatbotId }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      showSnackbar('Error loading categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color || '#3498db'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)]
      });
    }
    setDialog({ open: true, category });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Category name is required', 'error');
      return;
    }

    setSaving(true);
    try {
      if (dialog.category) {
        await axiosInstance.put(`/api/event-post-categories/${dialog.category.id}`, formData, {
          params: { chatbot_id: chatbotId }
        });
        showSnackbar('Category updated successfully', 'success');
      } else {
        await axiosInstance.post('/api/event-post-categories', formData, {
          params: { chatbot_id: chatbotId }
        });
        showSnackbar('Category created successfully', 'success');
      }

      setDialog({ open: false, category: null });
      loadCategories();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving category', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.category) return;

    try {
      await axiosInstance.delete(`/api/event-post-categories/${deleteDialog.category.id}`, {
        params: { chatbot_id: chatbotId }
      });
      showSnackbar('Category deleted successfully', 'success');
      setDeleteDialog({ open: false, category: null });
      loadCategories();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting category', 'error');
    }
  };

  if (checkingPermissions || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt:10 }}>
      {/* Snackbar */}
      {snackbar.open && (
        <Alert severity={snackbar.severity} sx={{ mb: 2 }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts`)}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon /> Post Categories
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Organize your posts with categories
            </Typography>
          </Box>
        </Box>

        {canModify && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Category
          </Button>
        )}
      </Box>

      {/* Categories Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Posts</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No categories found</Typography>
                  {canModify && (
                    <Button
                      sx={{ mt: 2 }}
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                    >
                      Create Your First Category
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              categories.map(category => (
                <TableRow key={category.id} hover>
                  <TableCell>
                    <Chip
                      label={category.name}
                      sx={{ bgcolor: category.color, color: '#fff' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {category.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: category.color,
                        border: '2px solid #ddd'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {category.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={category.post_count || 0} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {canModify && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(category)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, category })}
                            disabled={category.post_count > 0}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, category: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.category ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {PRESET_COLORS.map(color => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: color,
                      cursor: 'pointer',
                      border: formData.color === color ? '3px solid #333' : '2px solid transparent',
                      '&:hover': { opacity: 0.8 }
                    }}
                  />
                ))}
              </Box>
              <TextField
                size="small"
                label="Custom Color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                sx={{ mt: 2, width: 150 }}
                type="color"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, category: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, category: null })}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "<strong>{deleteDialog.category?.name}</strong>"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, category: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventPostCategories;
